import { Button, Stack, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import {
  AI_INTERVIEW_CREATED,
  AI_INTERVIEW_EVALUATED,
  AI_INTERVIEW_IN_PROGRESS,
  AI_INTERVIEW_LINK_SENT,
  ANALYZING,
  CANDIDATE_HIRED,
  CANDIDATE_REJECTED,
  IN_PERSON_INTERVIEW_SCHEDULED,
  SCREENING_COMPLETED,
  SCREENING_DISQUALIFIED,
  SCREENING_ERROR,
  SCREENING_QUALIFIED,
  CODING_INTERVIEW_SUBMITTED
} from 'src/sections/job-candidate/job-candidate-status-constants';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  pill: {
    color: theme.palette.content.primaryBlack1,
    cursor: 'pointer',
    borderRadius: 39,
    background: theme.palette.background.primaryGrey1,
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    height: 30,
    padding: '4px 10px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const STATUS_UI_MAPPING = {
  [ANALYZING]: 'Candidate added/ applied',
  [AI_INTERVIEW_CREATED]: 'AI Interview Created',
  [AI_INTERVIEW_LINK_SENT]: 'AI Interview Link Sent',
  [SCREENING_ERROR]: 'Uncategorized',
  [SCREENING_COMPLETED]: 'Screening Completed',
  [AI_INTERVIEW_IN_PROGRESS]: 'AI interview incomplete',
  [AI_INTERVIEW_EVALUATED]: 'AI interview completed',
  [IN_PERSON_INTERVIEW_SCHEDULED]: 'Interview',
  [CANDIDATE_REJECTED]: 'Rejected',
  [CANDIDATE_HIRED]: 'Hired',
  [SCREENING_DISQUALIFIED]: 'Disqualified',
  [SCREENING_QUALIFIED]: 'Qualified',
  [CODING_INTERVIEW_SUBMITTED]: 'Coding Interview Submitted',
};

const Pill = ({
  status,
  setFilterAPICode,
  setSelected,
  selected,
  FiltersMap,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  return (
    <Button
      onClick={() => {
        setFilterAPICode(prev => {
          const dum = { ...prev };
          if (selected === status) {
            delete dum[
              FiltersMap?.filter_codes[`SCREENING_STATUS_${selected}`]
            ];
          } else {
            delete dum[
              FiltersMap?.filter_codes[`SCREENING_STATUS_${selected}`]
            ];
            dum[FiltersMap?.filter_codes[`SCREENING_STATUS_${status}`]] = 1;
          }
          console.log(dum);
          return dum;
        });
        setSelected(prev => {
          if (prev === status) return '';
          return status;
        });
      }}
      variant="soft"
      size="small"
      className={classes.pill}
      sx={{
        border:
          selected === status &&
          `1px solid ${theme.palette.border.primaryAccent1} !important`,
        background:
          selected === status &&
          `${theme.palette.background.primaryAccent2} !important`,
      }}
    >
      <Typography variant="caption">{`${STATUS_UI_MAPPING?.[status]} (${FiltersMap?.status?.[status]?.candidate_count})`}</Typography>
    </Button>
  );
};

Pill.propTypes = {
  status: PropTypes.string.isRequired,
  setFilterAPICode: PropTypes.func.isRequired,
  setSelected: PropTypes.func.isRequired,
  selected: PropTypes.string.isRequired,
  FiltersMap: PropTypes.object.isRequired,
};

const StatusFilter = ({
  FiltersMap,
  filterAPICode,
  setFilterAPICode,
  reset,
}) => {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    setSelected('');
  }, [reset]);

  return (
    <Stack direction="row" spacing="8px" sx={{ flexWrap: 'wrap' }}>
      {FiltersMap?.status?.ANALYZING && (
        <Pill
          status={ANALYZING}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.AI_INTERVIEW_CREATED && (
        <Pill
          status={AI_INTERVIEW_CREATED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.AI_INTERVIEW_LINK_SENT && (
        <Pill
          status={AI_INTERVIEW_LINK_SENT}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.SCREENING_ERROR && (
        <Pill
          status={SCREENING_ERROR}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.SCREENING_COMPLETED && (
        <Pill
          status={SCREENING_COMPLETED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.AI_INTERVIEW_IN_PROGRESS && (
        <Pill
          status={AI_INTERVIEW_IN_PROGRESS}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.AI_INTERVIEW_EVALUATED && (
        <Pill
          status={AI_INTERVIEW_EVALUATED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.CODING_INTERVIEW_SUBMITTED && (
        <Pill
          status={CODING_INTERVIEW_SUBMITTED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.IN_PERSON_INTERVIEW_SCHEDULED && (
        <Pill
          status={IN_PERSON_INTERVIEW_SCHEDULED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.CANDIDATE_REJECTED && (
        <Pill
          status={CANDIDATE_REJECTED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.CANDIDATE_HIRED && (
        <Pill
          status={CANDIDATE_HIRED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.SCREENING_DISQUALIFIED && (
        <Pill
          status={SCREENING_DISQUALIFIED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
      {FiltersMap?.status?.SCREENING_QUALIFIED && (
        <Pill
          status={SCREENING_QUALIFIED}
          selected={selected}
          setSelected={setSelected}
          setFilterAPICode={setFilterAPICode}
          FiltersMap={FiltersMap}
        />
      )}
    </Stack>
  );
};

StatusFilter.propTypes = {
  FiltersMap: PropTypes.object.isRequired,
  filterAPICode: PropTypes.object.isRequired,
  setFilterAPICode: PropTypes.func.isRequired,
  reset: PropTypes.bool.isRequired,
};

export default StatusFilter;
